import {
  signalStore,
  withState,
  withMethods,
  withHooks,
  withComputed,
  patchState,
} from "@ngrx/signals";
import { computed, inject } from "@angular/core";
import { firstValueFrom, interval as rxInterval, Subscription } from "rxjs";
import {
  MarketDataApiService as GeneratedMarketDataService,
} from "@api/api/marketData.service";
import {
  SymbolsApiService as GeneratedSymbolsService,
} from "@api/api/symbols.service";
import {
  Symbol,
  SymbolListResponse,
  SymbolStatusDb,
  TickerResponseSchema,
  OrderBookResponseSchema,
  RecentMarketTradeSchema,
  MarketCandleSchema,
} from "@api/model/models";
import { LoggerService } from "../core/services/logger.service";
import { normalizeSymbolForApi } from "../shared/utils/symbol.utils";
import {
  marketDecimalNumberOrNull,
  parseMarketCandlesResponse,
  parseOrderBookResponse,
  parseRecentMarketTradesResponse,
  parseTickerResponse,
} from "../core/utils/market-data-api-contract.utils";

// Type aliases for cleaner code
type Ticker = TickerResponseSchema;
type OrderBook = OrderBookResponseSchema;
type Trade = RecentMarketTradeSchema;
type Candle = MarketCandleSchema;

interface MarketDataState {
  // Symbols
  symbols: Symbol[];
  symbolsLoading: boolean;
  symbolsError: string | null;
  selectedSymbol: Symbol | null;

  // Tickers
  tickers: Map<string, Ticker>;
  tickersLoading: boolean;
  tickersError: string | null;

  // Order Books
  orderBooks: Map<string, OrderBook>;
  orderBooksLoading: boolean;
  orderBooksError: string | null;

  // Trades
  recentTrades: Map<string, Trade[]>;
  tradesLoading: boolean;
  tradesError: string | null;

  // Candles
  candles: Map<string, Candle[]>;
  candlesLoading: boolean;
  candlesError: string | null;

  // Watchlist
  watchlist: string[];

  // Real-time subscription status
  subscriptions: Set<string>;
  connectionStatus: "connected" | "connecting" | "disconnected" | "error";

  // Settings
  autoRefresh: boolean;
  refreshInterval: number; // milliseconds
  defaultDepth: number; // orderbook depth
}

const initialState: MarketDataState = {
  // Symbols
  symbols: [],
  symbolsLoading: false,
  symbolsError: null,
  selectedSymbol: null,

  // Tickers
  tickers: new Map(),
  tickersLoading: false,
  tickersError: null,

  // Order Books
  orderBooks: new Map(),
  orderBooksLoading: false,
  orderBooksError: null,

  // Trades
  recentTrades: new Map(),
  tradesLoading: false,
  tradesError: null,

  // Candles
  candles: new Map(),
  candlesLoading: false,
  candlesError: null,

  // Watchlist
  watchlist: [],

  // Real-time subscription status
  subscriptions: new Set(),
  connectionStatus: "disconnected",

  // Settings
  autoRefresh: true,
  refreshInterval: 5000,
  defaultDepth: 20,
};

export const MarketDataStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed((store) => ({
    // Computed values for symbols
    activeSymbols: computed(() =>
      store.symbols().filter((s) => s.status === SymbolStatusDb.Active),
    ),
    symbolsByExchange: computed(() => {
      const symbols = store.symbols();
      const grouped = new Map<string, Symbol[]>();

      symbols.forEach((symbol) => {
        const exchange = symbol.exchange;
        if (!grouped.has(exchange)) {
          grouped.set(exchange, []);
        }
        grouped.get(exchange)!.push(symbol);
      });

      return grouped;
    }),

    // Computed values for tickers
    watchlistTickers: computed(() => {
      const tickers = store.tickers();
      const watchlist = store.watchlist();
      const result: Ticker[] = [];

      watchlist.forEach((symbol) => {
        if (!symbol) return;
        const ticker = tickers.get(normalizeSymbolForApi(symbol));
        if (ticker) {
          result.push(ticker);
        }
      });

      return result;
    }),

    topGainers: computed(() => {
      return Array.from(store.tickers().values())
        .map((t) => ({
          ticker: t,
          changePercent: marketDecimalNumberOrNull(t.changePercent24h),
        }))
        .filter(
          (item): item is { ticker: Ticker; changePercent: number } =>
            item.changePercent !== null,
        )
        .filter((item) => item.changePercent > 0)
        .sort((a, b) => b.changePercent - a.changePercent)
        .slice(0, 10)
        .map((item) => item.ticker);
    }),

    topLosers: computed(() => {
      return Array.from(store.tickers().values())
        .map((t) => ({
          ticker: t,
          changePercent: marketDecimalNumberOrNull(t.changePercent24h),
        }))
        .filter(
          (item): item is { ticker: Ticker; changePercent: number } =>
            item.changePercent !== null,
        )
        .filter((item) => item.changePercent < 0)
        .sort((a, b) => a.changePercent - b.changePercent)
        .slice(0, 10)
        .map((item) => item.ticker);
    }),

    mostActive: computed(() => {
      return Array.from(store.tickers().values())
        .map((t) => ({
          ticker: t,
          volume: marketDecimalNumberOrNull(t.volume24h),
        }))
        .filter(
          (item): item is { ticker: Ticker; volume: number } =>
            item.volume !== null,
        )
        .sort((a, b) => b.volume - a.volume)
        .slice(0, 10)
        .map((item) => item.ticker);
    }),

    // Computed values for orderbooks
    currentOrderBook: computed(() => {
      const selectedSymbol = store.selectedSymbol();
      if (!selectedSymbol?.symbol) {
        return null;
      }

      return (
        store.orderBooks().get(normalizeSymbolForApi(selectedSymbol.symbol)) ||
        null
      );
    }),

    // Computed values for trades
    currentTrades: computed(() => {
      const selectedSymbol = store.selectedSymbol();
      if (!selectedSymbol?.symbol) {
        return [];
      }

      return (
        store
          .recentTrades()
          .get(normalizeSymbolForApi(selectedSymbol.symbol)) || []
      );
    }),

    // Computed values for candles
    currentCandles: computed(() => {
      const selectedSymbol = store.selectedSymbol();
      if (!selectedSymbol?.symbol) {
        return [];
      }

      // Match the key format used by loadCandles: normalizedSymbol_interval
      // Default to '1h' to match the loadCandles default interval parameter
      const key = `${normalizeSymbolForApi(selectedSymbol.symbol)}_1h`;
      return store.candles().get(key) || [];
    }),

    // Connection state
    isConnected: computed(() => store.connectionStatus() === "connected"),
    isConnecting: computed(() => store.connectionStatus() === "connecting"),
    hasConnectionError: computed(() => store.connectionStatus() === "error"),

    // Loading states
    isLoading: computed(
      () =>
        store.symbolsLoading() ||
        store.tickersLoading() ||
        store.orderBooksLoading() ||
        store.tradesLoading() ||
        store.candlesLoading(),
    ),

    // Error states
    hasErrors: computed(
      () =>
        !!(
          store.symbolsError() ||
          store.tickersError() ||
          store.orderBooksError() ||
          store.tradesError() ||
          store.candlesError()
        ),
    ),
  })),
  withMethods((store) => {
    const marketDataApi = inject(GeneratedMarketDataService);
    const symbolsApi = inject(GeneratedSymbolsService);
    const logger = inject(LoggerService);
    // Invalidates symbol-list responses that complete after a tenant/logout
    // reset. Without this epoch, a slow request from tenant A can repopulate
    // the root store after the app has already switched to tenant B.
    let symbolLoadEpoch = 0;

    // GAP-FE-038: RxJS Subscription replaces raw setInterval — keeps the
    // start/stop semantics but routes timing through rxjs so the rest of the
    // codebase has a single timer abstraction.
    let refreshSubscription: Subscription | null = null;

    return {
      // ========== Symbols Methods ==========

      async loadSymbols(exchange?: string) {
        const requestEpoch = ++symbolLoadEpoch;
        patchState(store, { symbolsLoading: true, symbolsError: null });

        try {
          const response: SymbolListResponse = await firstValueFrom(
            symbolsApi.listSymbols0(undefined, exchange),
          );
          if (requestEpoch !== symbolLoadEpoch) {
            return;
          }

          patchState(store, {
            symbols: response.symbols,
            symbolsLoading: false,
          });

          logger.debug("Symbols loaded", { count: response.symbols.length });
        } catch (error: unknown) {
          if (requestEpoch !== symbolLoadEpoch) {
            return;
          }
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load symbols";
          logger.error("Failed to load symbols", { error });
          patchState(store, {
            symbolsLoading: false,
            symbolsError: errorMessage,
          });
        }
      },

      selectSymbol(symbol: Symbol | null) {
        patchState(store, { selectedSymbol: symbol });

        // Load market data for selected symbol
        if (symbol) {
          this.loadTickerForSymbol(symbol.symbol);
          this.loadOrderBookForSymbol(symbol.symbol);
          this.loadTradesForSymbol(symbol.symbol);
        }
      },

      // ========== Tickers Methods ==========

      async loadTickerForSymbol(symbol: string) {
        patchState(store, { tickersLoading: true, tickersError: null });

        try {
          const normalizedSymbol = normalizeSymbolForApi(symbol);
          const response = await firstValueFrom(
            marketDataApi.getTicker(normalizedSymbol),
          );
          const ticker = parseTickerResponse(response, normalizedSymbol);

          const tickers = new Map(store.tickers());
          tickers.set(normalizedSymbol, ticker);

          patchState(store, {
            tickers,
            tickersLoading: false,
          });

          logger.debug("Ticker loaded", { symbol });
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load ticker";
          const is404 =
            error &&
            typeof error === "object" &&
            "status" in error &&
            (error as { status: number }).status === 404;
          const tickers = new Map(store.tickers());
          tickers.delete(normalizeSymbolForApi(symbol));
          if (is404) {
            logger.warn(`Ticker not found for ${symbol}`, { symbol });
          } else {
            logger.error(`Failed to load ticker for ${symbol}`, {
              symbol,
              error: errorMessage,
            });
          }
          patchState(store, {
            tickers,
            tickersLoading: false,
            tickersError: is404
              ? `No ticker data is available for ${symbol}`
              : errorMessage,
          });
        }
      },

      async loadMultipleTickers(symbols: string[]) {
        patchState(store, { tickersLoading: true, tickersError: null });

        try {
          const results = await Promise.allSettled(
            symbols.map(async (symbol) => {
              const normalizedSymbol = normalizeSymbolForApi(symbol);
              const response = await firstValueFrom(
                marketDataApi.getTicker(normalizedSymbol),
              );
              return {
                symbol: normalizedSymbol,
                ticker: parseTickerResponse(response, normalizedSymbol),
              };
            }),
          );
          const tickers = new Map(store.tickers());
          const failedSymbols: string[] = [];

          results.forEach((result, index) => {
            const normalizedSymbol = normalizeSymbolForApi(symbols[index]);
            if (result.status === "fulfilled") {
              tickers.set(result.value.symbol, result.value.ticker);
            } else {
              tickers.delete(normalizedSymbol);
              failedSymbols.push(normalizedSymbol);
            }
          });

          patchState(store, {
            tickers,
            tickersLoading: false,
            tickersError:
              failedSymbols.length === 0
                ? null
                : `Ticker data unavailable for ${failedSymbols.join(", ")}`,
          });

          logger.debug("Multiple tickers loaded", {
            count: results.filter((result) => result.status === "fulfilled")
              .length,
            failedSymbols,
          });
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load tickers";
          logger.error("Failed to load tickers", { error });
          patchState(store, {
            tickersLoading: false,
            tickersError: errorMessage,
          });
        }
      },

      updateTicker(response: unknown) {
        const ticker = parseTickerResponse(response);
        const tickers = new Map(store.tickers());
        tickers.set(normalizeSymbolForApi(ticker.symbol), ticker);
        patchState(store, { tickers });
      },

      // ========== Order Book Methods ==========

      async loadOrderBookForSymbol(symbol: string, depth?: number) {
        patchState(store, { orderBooksLoading: true, orderBooksError: null });

        try {
          const normalizedSymbol = normalizeSymbolForApi(symbol);
          const response = await firstValueFrom(
            marketDataApi.getOrderbook(
              normalizedSymbol,
              depth || store.defaultDepth(),
            ),
          );
          const orderBook = parseOrderBookResponse(response, normalizedSymbol);

          const orderBooks = new Map(store.orderBooks());
          orderBooks.set(normalizedSymbol, orderBook);

          patchState(store, {
            orderBooks,
            orderBooksLoading: false,
          });

          logger.debug("Order book loaded", { symbol });
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load order book";
          const orderBooks = new Map(store.orderBooks());
          orderBooks.delete(normalizeSymbolForApi(symbol));
          logger.error(`Failed to load order book for ${symbol}`, {
            symbol,
            error: errorMessage,
          });
          patchState(store, {
            orderBooks,
            orderBooksLoading: false,
            orderBooksError: errorMessage,
          });
        }
      },

      updateOrderBook(orderBook: OrderBook) {
        if (!orderBook?.symbol) return;
        const orderBooks = new Map(store.orderBooks());
        orderBooks.set(normalizeSymbolForApi(orderBook.symbol), orderBook);
        patchState(store, { orderBooks });
      },

      // ========== Trades Methods ==========

      async loadTradesForSymbol(symbol: string, limit: number = 100) {
        patchState(store, { tradesLoading: true, tradesError: null });

        try {
          const response = await firstValueFrom(
            marketDataApi.marketDataGetTrades(
              normalizeSymbolForApi(symbol),
              undefined,
              undefined,
              limit,
            ),
          );
          const trades = parseRecentMarketTradesResponse(response, symbol).data
            .trades;

          const recentTrades = new Map(store.recentTrades());
          recentTrades.set(normalizeSymbolForApi(symbol), trades);

          patchState(store, {
            recentTrades,
            tradesLoading: false,
          });

          logger.debug("Trades loaded", { symbol, count: trades.length });
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load trades";
          const recentTrades = new Map(store.recentTrades());
          recentTrades.delete(normalizeSymbolForApi(symbol));
          logger.error(`Failed to load trades for ${symbol}`, {
            symbol,
            error: errorMessage,
          });
          patchState(store, {
            recentTrades,
            tradesLoading: false,
            tradesError: errorMessage,
          });
        }
      },

      addTrade(symbol: string, trade: Trade) {
        const recentTrades = new Map(store.recentTrades());
        const normalizedSymbol = normalizeSymbolForApi(symbol);
        const trades = recentTrades.get(normalizedSymbol) || [];

        // Add new trade to beginning and keep only last 100
        const updatedTrades = [trade, ...trades].slice(0, 100);
        recentTrades.set(normalizedSymbol, updatedTrades);

        patchState(store, { recentTrades });
      },

      // ========== Candles Methods ==========

      async loadCandles(
        symbol: string,
        interval:
          "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d" | "1w" | "1M" = "1h",
        limit: number = 100,
      ) {
        patchState(store, { candlesLoading: true, candlesError: null });

        try {
          const response = await firstValueFrom(
            marketDataApi.getCandles(
              normalizeSymbolForApi(symbol),
              interval,
              undefined,
              undefined,
              limit,
            ),
          );
          const candleData = parseMarketCandlesResponse(
            response,
            symbol,
            interval,
          ).data.candles;

          const candles = new Map(store.candles());
          const key = `${normalizeSymbolForApi(symbol)}_${interval}`;
          candles.set(key, candleData);

          patchState(store, {
            candles,
            candlesLoading: false,
          });

          logger.debug("Candles loaded", {
            symbol,
            interval,
            count: candleData.length,
          });
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to load candles";
          const candles = new Map(store.candles());
          candles.delete(`${normalizeSymbolForApi(symbol)}_${interval}`);
          logger.error(`Failed to load candles for ${symbol}`, {
            symbol,
            error: errorMessage,
          });
          patchState(store, {
            candles,
            candlesLoading: false,
            candlesError: errorMessage,
          });
        }
      },

      // ========== Watchlist Methods ==========

      addToWatchlist(symbol: string) {
        const watchlist = [...store.watchlist()];
        const normalizedNew = normalizeSymbolForApi(symbol);
        if (
          !watchlist.some((s) => normalizeSymbolForApi(s) === normalizedNew)
        ) {
          watchlist.push(symbol);
          patchState(store, { watchlist });

          // Load ticker for new watchlist item
          this.loadTickerForSymbol(symbol);

          logger.debug("Added to watchlist", { symbol });
        }
      },

      removeFromWatchlist(symbol: string) {
        const normalizedTarget = normalizeSymbolForApi(symbol);
        const watchlist = store
          .watchlist()
          .filter((s) => normalizeSymbolForApi(s) !== normalizedTarget);
        patchState(store, { watchlist });

        logger.debug("Removed from watchlist", { symbol });
      },

      clearWatchlist() {
        patchState(store, { watchlist: [] });
        logger.debug("Watchlist cleared");
      },

      // ========== Real-time Methods ==========

      setConnectionStatus(
        status: "connected" | "connecting" | "disconnected" | "error",
      ) {
        patchState(store, { connectionStatus: status });
      },

      subscribe(symbol: string) {
        const subscriptions = new Set(store.subscriptions());
        subscriptions.add(normalizeSymbolForApi(symbol));
        patchState(store, { subscriptions });

        logger.debug("Subscribed to symbol", { symbol });
      },

      unsubscribe(symbol: string) {
        const subscriptions = new Set(store.subscriptions());
        subscriptions.delete(normalizeSymbolForApi(symbol));
        patchState(store, { subscriptions });

        logger.debug("Unsubscribed from symbol", { symbol });
      },

      // ========== Auto-refresh Methods ==========

      startAutoRefresh() {
        refreshSubscription?.unsubscribe();

        const periodMs = store.refreshInterval();
        refreshSubscription = rxInterval(periodMs).subscribe(() => {
          // Refresh watchlist tickers
          const watchlist = store.watchlist();
          if (watchlist.length > 0) {
            this.loadMultipleTickers(watchlist);
          }

          // Refresh selected symbol data
          const selectedSymbol = store.selectedSymbol();
          if (selectedSymbol) {
            this.loadTickerForSymbol(selectedSymbol.symbol);
            this.loadOrderBookForSymbol(selectedSymbol.symbol);
            this.loadTradesForSymbol(selectedSymbol.symbol);
          }
        });

        patchState(store, { autoRefresh: true });
        logger.debug("Auto-refresh started", { interval: periodMs });
      },

      stopAutoRefresh() {
        refreshSubscription?.unsubscribe();
        refreshSubscription = null;

        patchState(store, { autoRefresh: false });
        logger.debug("Auto-refresh stopped");
      },

      setRefreshInterval(interval: number) {
        patchState(store, { refreshInterval: interval });

        // Restart auto-refresh if it's running
        if (store.autoRefresh()) {
          this.stopAutoRefresh();
          this.startAutoRefresh();
        }
      },

      // ========== Utility Methods ==========

      clearErrors() {
        patchState(store, {
          symbolsError: null,
          tickersError: null,
          orderBooksError: null,
          tradesError: null,
          candlesError: null,
        });
      },

      reset() {
        symbolLoadEpoch += 1;
        this.stopAutoRefresh();
        patchState(store, initialState);
      },
    };
  }),
  withHooks((store) => {
    const logger = inject(LoggerService);

    return {
      onInit() {
        logger.debug("Market data store initialized");

        // Load initial symbols
        store.loadSymbols();

        // Start auto-refresh
        store.startAutoRefresh();
      },
      onDestroy() {
        logger.debug("Market data store destroyed");

        store.stopAutoRefresh();
        store.reset();
      },
    };
  }),
);
